import { AssortmentPrices } from '@retail/my-assortment/types';
import { AppTFunction } from '@retail/app/i18n';
import { AssortmentExportColumn } from './AssortmentExportColumn';
import { getPriceSuggestionType } from '@retail/products/utils';
import dayjs from 'dayjs';

const formatNullableDate = (date: string | Date | null | undefined) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DD');
};

type AssortmentValueGetter = (
  assortmentItem: AssortmentPrices,
  t: AppTFunction
) => string | number | null | undefined;

export const assortmentPricesValueMap: Record<AssortmentExportColumn, AssortmentValueGetter> = {
  supplierItemPrimaryText: (item) => item.primaryText,
  mgItemNumber: (item) => item.mgItemNumber,
  finfoNumber: (item) => item.finfoNumber,
  nobbNumber: (item) => item.nobbNumber,
  primaryGtin: (item) => item.primaryGtin,
  supplierName: (item) => item.supplier.name,
  productGroupNumber: (item) => item.productGroup.groupNumber,
  discountGroupName: (item) => item.discountGroup?.name,
  purchaseConditionName: (item) => item.purchaseCondition?.agreementText,
  purchaseConditionLevel: (item) => item.purchaseCondition?.bindingTarget,
  purchasePrice: (item) => item.pricesExVat.purchasePrice,
  purchasePriceUnit: (item) => item.primaryUnit,
  purchasePriceDate: (item) => formatNullableDate(item.purchasePriceDate),
  futurePurchasePrice: (item) => item.pricesExVat.futurePurchasePrice,
  futurePurchasePriceUnit: (item) => item.primaryUnit,
  futurePurchasePriceDate: (item) => formatNullableDate(item.futurePurchasePriceDate),
  purchasePriceChangeAbsolute: (item) => {
    const currentPrice = item.pricesExVat.purchasePrice;
    const futurePrice = item.pricesExVat.futurePurchasePrice;

    if (!currentPrice || !futurePrice) return null;
    return futurePrice - currentPrice;
  },
  purchasePriceChangePercentage: (item) => {
    const currentPrice = item.pricesExVat.purchasePrice;
    const futurePrice = item.pricesExVat.futurePurchasePrice;

    if (!currentPrice || !futurePrice) return null;
    return ((futurePrice - currentPrice) / currentPrice) * 100;
  },
  freightValue: (item) => {
    const freightCondition = item.freightCondition;
    return freightCondition?.freightFixedAddOn ?? freightCondition?.freightPercentAddon;
  },
  freightValueDenomination: (item, t) => {
    const freightCondition = item.freightCondition;
    if (freightCondition?.freightFixedAddOn)
      return t('myAssortment.columns.freightCondition.fixedAddOn');
    if (freightCondition?.freightPercentAddon) return '%';
    return null;
  },
  freightUnit: (item) => item.freightCondition?.freightFixedAddOnUnit,
  costPrice: (item) => item.pricesExVat.costPrice,
  costPriceUnit: (item) => item.primaryUnit,
  costPriceDate: (item) => formatNullableDate(item.costPriceDate),
  costPriceInBasePriceUnit: (item) => item.pricesExVat.costPriceBasePriceUnit,
  costPriceBasePriceUnit: (item) => item.basePriceUnit,
  MGLSalesPrice: (item) => item.pricesExVat.markupOrPriceSuggestionPrice,
  MGLDG: (item) => item.markupOrPriceSuggestionCondition?.coverageDegree,
  customerFactor: (item) => item.customerMarkupCondition?.coverageDegree,
  salesPriceExVat: (item) => item.pricesExVat.salesPrice,
  salesPriceIncVat: (item) => item.pricesIncVat.salesPrice,
  salesPriceUnit: (item) => item.primaryUnit,
  salesPriceDate: (item) => formatNullableDate(item.salesPriceDate),
  salesPriceCoverage: (item) => item.coverageDegree,
  salesPriceDpakExVat: (item) => item.pricesExVat.salesPriceSecondary,
  salesPriceDpakIncVat: (item) => item.pricesIncVat.salesPriceSecondary,
  salesPriceDpakCoverage: (item) => item.coverageDegreeSecondary,
  salesPriceTpakExVat: (item) => item.pricesExVat.salesPriceTertiary,
  salesPriceTpakIncVat: (item) => item.pricesIncVat.salesPriceTertiary,
  salesPriceTpakCoverage: (item) => item.coverageDegreeTertiary,
  vatRate: (item) => '',
  futureSalesPriceExVat: (item) => item.pricesExVat.futureSalesPrice,
  futureSalesPriceIncVat: (item) => item.pricesIncVat.futureSalesPrice,
  futureSalesPriceUnit: (item) => item.primaryUnit,
  futureSalesPriceDate: (item) => formatNullableDate(item.futureSalesPriceDate),
  futureSalesPriceCoverage: (item) => item.futureCoverageDegree,
  salesPriceExVatChangeAbsolute: (item) => {
    const currentSalesPrice = item.pricesExVat.salesPrice;
    const futureSalesPrice = item.pricesExVat.futureSalesPrice;
    return currentSalesPrice && futureSalesPrice ? futureSalesPrice - currentSalesPrice : null;
  },
  salesPriceIncVatChangeAbsolute: (item) => {
    const currentSalesPrice = item.pricesIncVat.salesPrice;
    const futureSalesPrice = item.pricesExVat.salesPrice;
    return currentSalesPrice && futureSalesPrice ? futureSalesPrice - currentSalesPrice : null;
  },
  salesPriceChangePercantage: (item) => {
    const currentSalesPrice = item.pricesIncVat.salesPrice;
    const futureSalesPrice = item.pricesExVat.salesPrice;
    return currentSalesPrice && futureSalesPrice
      ? ((futureSalesPrice - currentSalesPrice) / currentSalesPrice) * 100
      : null;
  },
  priceConditionOrigin: (item, t) => {
    const priceOrigin = item.markupOrPriceSuggestionCondition?.type;
    if (!priceOrigin) return null;

    if (priceOrigin === 'PRICE_SUGGESTION') return t('priceSuggestion.title');

    return t('markup.markup');
  },
  priceSuggestionType: (item, t) =>
    item.markupOrPriceSuggestionCondition?.priceSuggestionType &&
    getPriceSuggestionType(t, item.markupOrPriceSuggestionCondition.priceSuggestionType),
  priceConditionLevel: (item) => item.markupOrPriceSuggestionCondition?.bindingTarget,
  assortmentDate: (item) => formatNullableDate(item.priority.validFrom),
  expiresFromAssortmentDate: (item) => formatNullableDate(item.priority.validTo),
  assortmentSource: (item) => '',
  finfoExpiresDate: (item) => formatNullableDate(item.discontinuationDate),
  finfoExpiresStatus: (item) => item.discontinuationStatus,
  addedBy: (item) => item.priority.addedBy,
};
